import { useMemo, useRef } from 'react';

import PropTypes from 'prop-types';

import ThreadDateGroup from '../ThreadDateGroup';
import Toast from '../Toast';
import * as Styled from './ThreadSidebar.style';

/**
 * Collapsible sidebar component that displays thread history organized by date
 * @param {object} props - Component props
 * @param {Array} props.threads - Array of thread objects to display
 * @param {string} props.currentThreadUuid - UUID of the currently active thread
 * @param {Function} props.onThreadSelect - Callback when a thread is selected
 * @param {boolean} [props.loading=false] - Whether threads are currently loading
 * @returns {import('react').ReactElement|null} The ThreadSidebar component
 */
function ThreadSidebar({
  // isVisible,
  threads = [],
  currentThreadUuid,
  onThreadSelect,
  loading = false
}) {
  const toastRef = useRef(null);

  // Handle errors from title updates
  const handleError = (errorMessage) => {
    if (toastRef.current) {
      toastRef.current.show(errorMessage);
    }
  };

  // Group threads by date for display
  const groupedThreads = useMemo(() => {
    if (!threads.length) return [];

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
    const lastWeek = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    const lastMonth = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);

    const groups = {
      today: [],
      yesterday: [],
      lastWeek: [],
      lastMonth: [],
      older: []
    };

    threads.forEach((thread) => {
      const threadDate = new Date(thread.createdAt || thread.lastMessageAt);
      const threadDateOnly = new Date(
        threadDate.getFullYear(),
        threadDate.getMonth(),
        threadDate.getDate()
      );

      if (threadDateOnly.getTime() === today.getTime()) {
        groups.today.push(thread);
      } else if (threadDateOnly.getTime() === yesterday.getTime()) {
        groups.yesterday.push(thread);
      } else if (threadDate >= lastWeek) {
        groups.lastWeek.push(thread);
      } else if (threadDate >= lastMonth) {
        groups.lastMonth.push(thread);
      } else {
        groups.older.push(thread);
      }
    });

    return [
      { key: 'today', label: 'Today', threads: groups.today },
      { key: 'yesterday', label: 'Yesterday', threads: groups.yesterday },
      { key: 'lastWeek', label: 'Previous 7 days', threads: groups.lastWeek },
      { key: 'lastMonth', label: 'Previous 30 days', threads: groups.lastMonth },
      { key: 'older', label: 'Older', threads: groups.older }
    ].filter((group) => group.threads.length > 0);
  }, [threads]);

  // if (!isVisible) {
  //   return null;
  // }

  // Handle the content rendering without nested ternary
  let sidebarContent;
  if (loading) {
    sidebarContent = (
      <Styled.LoadingContainer>
        <Styled.LoadingText>Loading threads...</Styled.LoadingText>
      </Styled.LoadingContainer>
    );
  } else if (threads.length === 0) {
    sidebarContent = (
      <Styled.EmptyStateContainer>
        <Styled.EmptyStateText>
          No previous threads found. Start a conversation to see your thread history here.
        </Styled.EmptyStateText>
      </Styled.EmptyStateContainer>
    );
  } else {
    sidebarContent = (
      <Styled.ThreadGroups>
        {groupedThreads.map((group) => (
          <ThreadDateGroup
            key={group.key}
            currentThreadUuid={currentThreadUuid}
            defaultExpanded
            label={group.label}
            threads={group.threads}
            onError={handleError}
            onThreadSelect={onThreadSelect}
          />
        ))}
      </Styled.ThreadGroups>
    );
  }

  return (
    <>
      <Styled.SidebarContainer data-testid="thread-sidebar">
        <Styled.SidebarContent>{sidebarContent}</Styled.SidebarContent>
      </Styled.SidebarContainer>
      <Toast ref={toastRef} />
    </>
  );
}

ThreadSidebar.propTypes = {
  // isVisible: PropTypes.bool.isRequired,
  threads: PropTypes.arrayOf(
    PropTypes.shape({
      threadUuid: PropTypes.string.isRequired,
      title: PropTypes.string,
      createdAt: PropTypes.string.isRequired,
      lastMessageAt: PropTypes.string,
      messageCount: PropTypes.number,
      snippet: PropTypes.string
    })
  ),
  currentThreadUuid: PropTypes.string,
  onThreadSelect: PropTypes.func.isRequired,
  loading: PropTypes.bool
};

export default ThreadSidebar;
